/**
 * m-quick-add-variant-sync.js
 *
 * Isolated fix for the quick-add variant race condition, kept out of the core
 * theme files (events.js / product-form.js / variant-picker.js) so the theme
 * stays upstream-updatable.
 *
 * The bug: changing a variant triggers an async section fetch, but core only
 * updates the hidden `input[name="id"]` once that fetch resolves. Core's
 * `handleSubmit` reads `new FormData(form)` synchronously, so a fast "Add to
 * cart" click during the in-flight fetch submits the stale (default) variant.
 *
 * The fix: keep the hidden variant input in sync with the picker's live
 * selection. We read the checked option's `data-variant-id` straight from the
 * DOM (already present in core markup) and write it onto the form's hidden
 * input — both continuously on `change` and, as a safety net, right before any
 * submit. This makes the submitted FormData (and core's queued-add fallback,
 * which reads `input.value`) always reflect the option the shopper sees.
 *
 * Loaded globally (deferred) via snippets/m-scripts.liquid. Self-activating:
 * it only acts where a `variant-picker` and `product-form-component` coexist.
 */
(() => {
  // Mirrors core's scope lookup: a picker and its form live in the same
  // dialog / section / product card (quick-add modal, PDP, card, etc.).
  const SCOPE_SELECTOR = 'dialog, .shopify-section, product-card';

  /**
   * Resolves the variant id currently selected in a picker, reading directly
   * from the DOM. Returns undefined when there is no resolved variant or when
   * multiple checked options disagree (multi-option products mid-change), so we
   * never write a guessed/incorrect variant and instead let core's async flow win.
   *
   * @param {Element} picker - The variant-picker element.
   * @returns {string | undefined}
   */
  const resolveVariantId = (picker) => {
    const selectedOptions = picker.querySelectorAll(
      'fieldset input[type="radio"]:checked[data-variant-id], select option:checked[data-variant-id]'
    );

    if (selectedOptions.length === 0) return undefined;

    /** @type {string | undefined} */
    let variantId;

    for (const option of selectedOptions) {
      const id = option instanceof HTMLElement ? option.dataset.variantId : undefined;
      if (!id) return undefined;

      if (variantId === undefined) {
        variantId = id;
      } else if (variantId !== id) {
        return undefined;
      }
    }

    return variantId;
  };

  /**
   * Writes the resolved variant id onto a form's hidden `input[name="id"]`.
   *
   * @param {Element} formScope - Element to search within for the hidden input.
   * @param {string} variantId - The variant id to set.
   */
  const writeVariantId = (formScope, variantId) => {
    const input = formScope.querySelector('input[name="id"]');
    if (!(input instanceof HTMLInputElement)) return;
    if (input.value === variantId) return;

    input.value = variantId;
  };

  /**
   * Syncs every product form in the picker's scope with the picker's selection.
   *
   * @param {Element} picker - The variant-picker that changed.
   */
  const syncFromPicker = (picker) => {
    const variantId = resolveVariantId(picker);
    if (!variantId) return;

    const scope = picker.closest(SCOPE_SELECTOR) ?? document;
    const forms = scope.querySelectorAll('product-form-component');

    for (const form of forms) {
      writeVariantId(form, variantId);
    }
  };

  // Continuous sync: the moment a variant option changes, the checked radio is
  // already updated synchronously, so the hidden input is correct long before
  // the shopper can click "Add to cart". This alone defeats the race.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const picker = target.closest('variant-picker');
      if (!picker) return;

      syncFromPicker(picker);
    },
    { capture: true }
  );

  // Safety net: reconcile the input immediately before submit, covering cases
  // where no `change` fired (programmatic submit) or a re-render reset the input.
  // Capture on `window` runs before core's document-level capture handler in
  // component.js, so the input is correct when core reads FormData.
  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLElement)) return;

      const formComponent = form.closest('product-form-component');
      if (!formComponent) return;

      const scope = formComponent.closest(SCOPE_SELECTOR) ?? document;
      const picker = scope.querySelector('variant-picker');
      if (!picker) return;

      const variantId = resolveVariantId(picker);
      if (!variantId) return;

      writeVariantId(formComponent, variantId);
    },
    { capture: true }
  );
})();
